import {
  fetchBaseQuery,
  type BaseQueryApi,
  type FetchArgs,
  type BaseQueryExtraOptions,
} from '@reduxjs/toolkit/query/react';
import { type RootState } from '@/app/store/store';
import { resetSession } from '@/features/authentication/authenticationSlice';
import { setAppNotice } from '@/app/store/appShellSlice';

// Enviar logs a un endpoint de auditoría si aplica (placeholder opcional)
const sendLogToServer = async (_logData: unknown) => {
  // INFO: Dejamos el hook listo para instrumentar si es necesario.
  // Ejemplo: await fetch('/api/logs', { method: 'POST', body: JSON.stringify(logData) });
};

// Crea una instancia de fetchBaseQuery con baseUrl específico y encabezados comunes.
// Cambio: eliminada la lógica de "isbiometrics". A partir de ahora, si existe
// un AccessToken en el estado (authentication), se envía como Bearer en Authorization.
const createConfiguredBaseQuery = (customBaseUrl: string) =>
  fetchBaseQuery({
    baseUrl: customBaseUrl,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state?.authentication?.AccessToken;
      // Allow endpoints to opt-out of Authorization header by setting a hint
      const skipAuth = (headers.get('x-skip-auth') || headers.get('X-Skip-Auth')) === '1';
      if (skipAuth) {
        try { headers.delete('x-skip-auth'); headers.delete('X-Skip-Auth'); } catch {}
      } else if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
    validateStatus: (response, body) => {
      // Éxito SOLO si 2xx y success !== false (según ApiResponseBase del OpenAPI)
      return response.status >= 200 && response.status < 300 && body?.success !== false;
    },
  });

/**
 * Retorna una funcion de consulta base (base query) que envuelve fetchBaseQuery
 * con logica de registro y permite un baseUrl dinamico.
 *
 * @param dynamicBaseUrl El URL base para las solicitudes de esta instancia de base query.
 * @returns Una consulta base compatible con Redux Toolkit Query.
 */
const dynamicBaseQueryWithLogger =
  (dynamicBaseUrl: string) =>
  async (
    args: string | FetchArgs, // 'args' puede ser una cadena URL o un objeto FetchArgs
    contextApi: BaseQueryApi,
    // El tipo de extraOptions ahora se basa en el tipo de retorno de createConfiguredBaseQuery
    extraOptions: BaseQueryExtraOptions<ReturnType<typeof createConfiguredBaseQuery>>,
  ) => {
    // Crea una instancia de base query con el dynamicBaseUrl proporcionado para esta solicitud.
    const baseQueryInstance = createConfiguredBaseQuery(dynamicBaseUrl);

    // Asegura que 'args' sea un objeto si inicialmente es una cadena.
    const processedArgs = typeof args === 'string' ? { url: args } : args;

    const params = {
      ...processedArgs,
      url: processedArgs.url,
    };
    const timestamp = new Date().toISOString();

    // Registro de log de la solicitud
    const requestLog = {
      type: 'REQUEST',
      timestamp,
      action: `${contextApi.endpoint}-pending`,
      status: 'pending',
      params,
      // Nota: Para Reparoly no usamos bookingNumber. Se deja fuera para no confundir.
    };

    await sendLogToServer(requestLog);

    // Para endpoints sensibles a CORS (refresh), evita encabezados no necesarios
    let finalArgs = args as FetchArgs;
    if (typeof args !== 'string') {
      const urlPath = String(processedArgs.url || '');
      if (urlPath.startsWith('/v1/auth/refresh')) {
        const headers: Record<string, string> = Object.assign(
          {},
          (processedArgs.headers as any) || {},
          { 'x-skip-auth': '1' },
        );
        finalArgs = { ...processedArgs, headers } as FetchArgs;
      } else {
        finalArgs = processedArgs as FetchArgs;
      }
    }

    // Usa la instancia de base query actual, que ya tiene el baseUrl dinamico configurado.
    const result = await baseQueryInstance(finalArgs, contextApi, extraOptions);

    // Helpers for unauthorized handling without silent refresh
    const isAuthPath = (p: string) =>
      p.startsWith('/v1/auth/login') ||
      p.startsWith('/v1/auth/refresh') ||
      p.startsWith('/v1/auth/signup') ||
      p.startsWith('/v1/auth/logout');
    const looksUnauthorized = (error: any) => {
      const status = Number(error?.status || 0);
      const msg = String(error?.data?.message || '').toLowerCase();
      return (
        status === 401 ||
        msg.includes('token inválido') ||
        msg.includes('token invalido') ||
        msg.includes('invalid token') ||
        msg.includes('jwt expired')
      );
    };
    const classifyReason = (message: string): 'session-evicted' | 'refresh-invalid' | 'session-expired' => {
      const msg = message.toLowerCase();
      if (msg.includes('refresh no activo')) return 'session-evicted';
      if (msg.includes('refresh inválido') || msg.includes('refresh invalido') || msg.includes('tipo inválido') || msg.includes('tipo invalido')) {
        return 'refresh-invalid';
      }
      return 'session-expired';
    };
    const reasonToMessage = (
      reason: 'session-evicted' | 'refresh-invalid' | 'session-expired',
    ): string => {
      switch (reason) {
        case 'session-evicted':
          return 'Tu sesión en este dispositivo se cerró porque superaste el límite de dispositivos. Vuelve a iniciar sesión.';
        case 'refresh-invalid':
          return 'No pudimos renovar tu sesión. Por favor, ingresa nuevamente.';
        case 'session-expired':
        default:
          return 'Tu sesión expiró. Por favor, inicia sesión nuevamente.';
      }
    };

    const urlPath = String((typeof args === 'string' ? args : args.url) || '');
    if ((result as any)?.error && !isAuthPath(urlPath)) {
      const err = (result as any).error as any;
      if (looksUnauthorized(err)) {
        // try { contextApi.dispatch(resetSession()); } catch {}
        try {
          const reason = classifyReason(String(err?.data?.message || ''));
          const msg = reasonToMessage(reason);
          contextApi.dispatch(
            setAppNotice({
              message: msg,
              status: Number(err?.status || 401),
              details: '',
              messageFormatted: '',
              action: 'auth.sessionExpired',
              endpointName: String((contextApi as any)?.endpoint || ''),
              validationErrors: {},
              redirectTo: `/auth?reason=${reason}`,
            }),
          );
        } catch {}
      }
    }

    const responseLog = {
      type: 'RESPONSE',
      timestamp,
      action: `${contextApi.endpoint}-fulfilled`,
      status: 'fulfilled',
      result: result.data,
    };

    sendLogToServer(responseLog);

    return result;
  };

// Exporta la funcion que retorna la base query.
// De esta manera, puedes pasar el 'baseUrl' dinámicamente cuando defines tus slices de API.
// Ejemplo de uso en un slice de API:
// const yourApi = createApi({
//   reducerPath: 'yourApi',
//   baseQuery: dynamicBaseQueryWithLogger(`${import.meta.env.VITE_APP_API_BIOMETRICS_MAJESTIC}`),
//   endpoints: (builder) => ({
//     // ... tus endpoints
//   }),
// });
export default dynamicBaseQueryWithLogger;
